/**
 * Browser-Autofill in der Fahrterfassung unterdrücken.
 *
 * Zwei verschiedene Mechanismen des Browsers stören hier, und sie brauchen
 * unterschiedliche Gegenmittel:
 *
 * 1. **Gespeicherte Profildaten** (Kontakte, Adressen). Dagegen hilft ein
 *    `autocomplete`-Wert, den der Browser nicht kennt – dann greift keine der
 *    eingebauten Feldheuristiken. `autocomplete="off"` genügt nicht: Bei
 *    Feldern, die nach Name, Telefon oder Adresse aussehen, behandelt Chrome
 *    das Attribut nur als Vorschlag.
 *
 * 2. **Zuvor selbst getippte Werte.** Die merkt sich der Browser pro Feld und
 *    bietet sie beim nächsten Öffnen wieder an. Erkannt wird das Feld über
 *    `name` und `id`. Bleiben die stabil, findet der Browser seine Einträge
 *    wieder – auch mit unbekanntem `autocomplete`. Dagegen hilft nur, die
 *    Kennung bei jedem Öffnen zu wechseln.
 *
 * Bewusst nicht `new-password`: Das unterdrückt zwar ebenfalls, provoziert
 * aber die Passwort-speichern-Abfrage des Browsers.
 */
export const NO_AUTOFILL = 'nc-no-autofill';

/**
 * Feldkennung, die sich bei jedem Öffnen des Formulars ändert. Damit läuft die
 * Feld-Historie des Browsers ins Leere, während Label-Verknüpfung und
 * Barrierefreiheit erhalten bleiben – Label und Eingabefeld teilen sich
 * weiterhin dieselbe Kennung, sie ist nur nicht mehr wiedererkennbar.
 */
export const createFieldIdFactory = () => {
  const salt = Math.random().toString(36).slice(2, 10);
  return (fieldName) => `${fieldName}-${salt}`;
};

/**
 * Hält `autocomplete` und `name` am internen `<input>` von Googles
 * `<gmp-place-autocomplete>`.
 *
 * Das Feld liegt im Shadow DOM und wird von der Komponente selbst verwaltet:
 * Google setzt seine Attribute nach dem Aufbau erneut und tauscht das Element
 * bei manchen Zustandswechseln aus. Ein einmaliges Setzen hält deshalb nicht –
 * die Attribute müssen überwacht und nachgezogen werden.
 *
 * Gibt eine Aufräumfunktion zurück.
 */
export const keepNoAutofillOnShadowInput = (element, fieldId = NO_AUTOFILL) => {
  if (!element) return () => {};

  let observer = null;
  let cancelled = false;

  const apply = () => {
    try {
      const input = element.shadowRoot?.querySelector('input');
      if (!input) return false;
      // Nur schreiben, wenn nötig – sonst löst die Änderung den Observer
      // erneut aus und wir drehen uns im Kreis.
      if (input.getAttribute('autocomplete') !== NO_AUTOFILL) {
        input.setAttribute('autocomplete', NO_AUTOFILL);
      }
      if (input.getAttribute('name') !== fieldId) {
        input.setAttribute('name', fieldId);
      }
      return true;
    } catch (error) {
      // Ändert Google die interne Struktur, bleibt es beim Browser-Standard –
      // die Adresssuche selbst funktioniert weiter.
      console.warn('Autofill konnte im Adressfeld nicht deaktiviert werden:', error);
      return true; // nicht endlos weiterversuchen
    }
  };

  const startObserving = () => {
    if (cancelled || !element.shadowRoot || observer) return;
    observer = new MutationObserver(apply);
    observer.observe(element.shadowRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['autocomplete', 'name'],
    });
  };

  // Der Shadow DOM existiert erst, wenn die Komponente sich aufgebaut hat.
  const waitForInput = () => {
    if (cancelled) return;
    if (apply()) {
      startObserving();
      return;
    }
    requestAnimationFrame(waitForInput);
  };

  requestAnimationFrame(waitForInput);

  return () => {
    cancelled = true;
    observer?.disconnect();
    observer = null;
  };
};
